/**
 * The one address the site gives out, and the reason it is its own module.
 *
 * `lib/site.ts` would be the obvious home, and it cannot be: every constant in
 * that file reads `import.meta.env`, which the WORKER tsconfig has no types for
 * (`tsconfig.worker.json` carries no Vite client types, by design — worker code
 * gets its configuration from bindings, not from the build). Importing
 * `lib/site` into `worker/tools.ts` fails the worker typecheck with five
 * `Property 'env' does not exist on type 'ImportMeta'`. So this value, which
 * both halves need and neither should re-type, lives in a module with no
 * dependencies and no environment reads at all.
 */

/**
 * The address a visitor is given when the automated path has already failed.
 *
 * **It is `@digitopia.co`, not `@digitopia.ai`, and that is not a typo.**
 * `digitopia.ai` does not receive mail. Its only MX record is
 * `ms19534464.msv1.invalid` — the placeholder Microsoft 365 creates when a
 * domain has been added for verification but mail flow was never configured,
 * and `.invalid` is a reserved TLD that can never resolve. So every message
 * sent to `info@digitopia.ai` bounces.
 *
 * Found on 2026-09-01, an hour after the site went live on its own domain, by
 * reading the zone rather than by anybody writing in — which is the whole
 * problem with a dead fallback address: the failure is silent on both ends.
 *
 * All three uses are last resorts — the two "we could not record that
 * automatically" panels in `ContactForm`, and the `<noscript>` on a legacy
 * HubSpot form — plus the Worker's refusal when `TURNSTILE_SECRET` is unset
 * (`worker/tools.ts`). That is what made it worth a shared constant: an address
 * shown *only* when everything else broke is the one place a bounce costs a
 * real lead, and it was drifting in four literals across three files.
 *
 * If `digitopia.ai` ever gets real MX records, this can move — but move it
 * here, once, and check the MX first.
 */
export const CONTACT_FALLBACK_EMAIL = 'info@digitopia.co'
